"use client";
import React, { useState, useRef, useCallback } from "react";
import { usePathname } from "next/navigation";
import { InstantSearch, Configure } from "react-instantsearch";
import { SearchContextProvider } from "@/components/Search/components/SearchContextProvider";
import { PRODUCT_SUMMARY_INDEX_EN } from "@/constants/algolia";
import getPublicClient from "@/lib/algolia/getPublicClient";
import { Filters } from "./components/GetStartedFilters";
import { Results } from "./components/GetStartedResults";
import MonarchResolver from "@/monarch/MonarchResolver";
import { GetStartedWithTestResult } from "@/monarch/rules/getStartedWith/types";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { cn } from "@/lib/utils";
import useIsTablet from "@/hooks/useIsTablet";
import { getSubjectFromPath } from "./utils/getPageSubject";
import { getSkillFromPath } from "./utils/getPageSkill";

const searchClient = getPublicClient();

const GetStartedContent = ({
  indexName,
  monarchData,
  defaultSubject,
  defaultSkill,
}: {
  indexName: string;
  monarchData: GetStartedWithTestResult;
  defaultSubject?: string;
  defaultSkill?: string;
}) => {
  const { data } = monarchData;
  const { filtersSection, resultsSection } = data || {};
  const [showResults, setShowResults] = useState(false);
  const [skillsEmpty, setSkillsEmpty] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isTablet = useIsTablet();
  const scrollToTop = useCallback(() => {
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const handleSkillsEmptyChange = useCallback((isEmpty: boolean) => {
    setSkillsEmpty(isEmpty);
  }, []);

  const handleExploreButtonClick = () => {
    if (showResults) {
      // Edit selections - hide results and scroll to top
      setShowResults(false);
      scrollToTop();
    } else {
      // Explore courses - show results
      setShowResults(true);
    }
  };

  const handleFilterChange = () => {
    // Hide results when filters change, requiring user to click "Explore courses" again
    if (showResults) {
      setShowResults(false);
    }
  };

  const handleEditSelections = () => {
    setShowResults(false);
    scrollToTop();
  };

  return (
    <div
      className={cn(
        "bg-putty-300 pt-8 px-4 not-prose",
        isTablet ? "mx-auto w-full" : "mx-break-out",
      )}
      ref={containerRef}
    >
      <div className="max-w-[1200px] mx-auto">
        {filtersSection && (
          <Filters
            indexName={indexName}
            showResults={showResults}
            onButtonClick={handleExploreButtonClick}
            onFilterChange={handleFilterChange}
            filtersMonarchData={filtersSection}
            isCompact={isTablet}
            onSkillsEmptyChange={handleSkillsEmptyChange}
            defaultSubject={defaultSubject}
            defaultSkill={defaultSkill}
          />
        )}
        {filtersSection && resultsSection && (
          <Results
            indexName={indexName}
            showResults={showResults}
            onEditSelections={handleEditSelections}
            resultsSectionMonarchData={resultsSection}
            isCompact={isTablet}
            skillsEmpty={skillsEmpty}
          />
        )}
      </div>
    </div>
  );
};

const GET_STARTED_WITH_CONFIG = {
  hitsPerPage: 4,
  facets: ["subject", "skills.skill"],
  clickAnalytics: true,
  indexName: PRODUCT_SUMMARY_INDEX_EN,
};

export const GetStartedWithComponent = ({
  monarchData,
  defaultSubject,
  defaultSkill,
}: {
  monarchData?: GetStartedWithTestResult;
  defaultSubject?: string;
  defaultSkill?: string;
}) => {
  const indexName = PRODUCT_SUMMARY_INDEX_EN;

  if (!monarchData) {
    return null;
  }

  return (
    <SearchContextProvider searchIndex={indexName}>
      <InstantSearch
        searchClient={searchClient}
        indexName={indexName}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
      >
        <Configure {...GET_STARTED_WITH_CONFIG} />
        <GetStartedContent
          indexName={indexName}
          monarchData={monarchData}
          defaultSubject={defaultSubject}
          defaultSkill={defaultSkill}
        />
      </InstantSearch>
    </SearchContextProvider>
  );
};

type GetStartedWithProps = {
  /**
   * Optional override that lets a CMS author pin the pre-selected subject
   * via the `[get-started-with subject="..."]` shortcode attribute. When
   * omitted the subject is derived from the current Learn page URL.
   */
  subject?: string;
  /**
   * Optional override that lets a CMS author pin the pre-selected skill
   * via the `[get-started-with skill="..."]` shortcode attribute. When
   * omitted the skill is derived from the current Learn page URL (the
   * page topic itself, e.g. /learn/python -> "Python").
   */
  skill?: string;
};

export const GetStartedWith = ({
  subject,
  skill,
}: GetStartedWithProps = {}) => {
  const pathname = usePathname();
  const defaultSubject =
    (typeof subject === "string" && subject.trim()) ||
    getSubjectFromPath(pathname) ||
    undefined;
  const defaultSkill =
    (typeof skill === "string" && skill.trim()) ||
    getSkillFromPath(pathname) ||
    undefined;

  return (
    <MonarchResolver<GetStartedWithTestResult>
      experimentKey="useGetStartedWithModule"
      variants={{
        control: null,
        test: (
          <MonarchDataConsumer<GetStartedWithTestResult>
            experimentKey="useGetStartedWithModule"
            render={(testResult) => (
              <GetStartedWithComponent
                monarchData={testResult}
                defaultSubject={defaultSubject}
                defaultSkill={defaultSkill}
              />
            )}
          />
        ),
      }}
    />
  );
};
